import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  DatePicker,
  EmptyState,
  Input,
  ReportTable,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { buildReportPopulationQuery } from '../components/reporting/reportPopulation';
import {
  getStoredIncludeImportedPatients,
  setStoredIncludeImportedPatients,
} from '../utils/populationPreferences';
import { PermissionSlug } from '../constants/featureFlags';
import { useDebounce } from '../hooks/useDebounce';
import {
  exportLateStartsReportReportsLateStartsExportGet,
  getLateStartsOptionsReportsLateStartsOptionsGet,
  getLateStartsReportReportsLateStartsGet,
} from '../services/api/sdk.gen';
import type {
  LateStartsReportResult,
  LateStartsReportRow,
  LateStartsSortBy,
  SortOrder,
} from '../services/api/types.gen';
import { useStore } from '../store';
import { useReportsBackHref } from '../reporting/backHref';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  formatDateToISO,
  getTodayInPT,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import {
  formatReportEmptyValue,
  formatReportDate,
  readChoiceParam,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import { ReportLink } from '../components/reporting/ReportLink';
import { patientChartPath } from '../utils/navigation';
import ReportsPermissionAlert from './ReportsPermissionAlert';

const ALL_CLINICS = '__all_clinics__';
const SORT_FIELDS: LateStartsSortBy[] = [
  'clinic_name',
  'patient_last_name',
  'patient_first_name',
  'date_of_birth',
  'mrn',
  'phone',
  'soc_date',
  'admission_start_date',
  'original_intake_date',
  'delay_days',
];
const DIRECTIONS: SortOrder[] = ['asc', 'desc'];

type ClinicOption = { id: string; name: string };

type LateStartsOptions = { clinics: ClinicOption[] };

const columns: ReportTableColumn<LateStartsReportRow>[] = [
  {
    id: 'clinic_name',
    header: 'Clinic',
    accessor: row => formatReportEmptyValue(row.clinic_name),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'patient_last_name',
    header: 'Patient Last',
    accessor: 'patient_last_name',
    render: (_value, row) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {row.patient_last_name}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'patient_first_name',
    header: 'Patient First',
    accessor: 'patient_first_name',
    render: (_value, row) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {row.patient_first_name}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'date_of_birth',
    header: 'DOB',
    accessor: row => formatReportDate(row.date_of_birth),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: row => formatReportEmptyValue(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'phone',
    header: 'Phone',
    accessor: row => formatReportEmptyValue(row.phone),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'soc_date',
    header: 'Admission SOC Date',
    accessor: row => formatReportDate(row.soc_date),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'admission_start_date',
    header: 'Admission Start Date',
    accessor: row => formatReportDate(row.admission_start_date),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'original_intake_date',
    header: 'Original Intake Date',
    accessor: row => formatReportDate(row.original_intake_date),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'delay_days',
    header: 'SOC minus Admission Start (Days)',
    accessor: 'delay_days',
    enableSorting: true,
    align: 'right',
    minWidth: 250,
  },
];

const INTEGER_DELAY_PATTERN = /^-?\d+$/;

function readIntegerDelayParam(value: string | null): string {
  return value && INTEGER_DELAY_PATTERN.test(value) ? value : '';
}

function defaultDateRange() {
  const today = getTodayInPT();
  const from = parseISOToLocalDate(today);
  if (!from) return { from: today, to: today };
  from.setDate(from.getDate() - 30);
  return { from: formatDateToISO(from), to: today };
}

export default function LateStartsReport() {
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  const reportsBackHref = useReportsBackHref();
  const [searchParams, setSearchParams] = useSearchParams();
  const [options, setOptions] = useState<LateStartsOptions>({ clinics: [] });
  const [report, setReport] = useState<LateStartsReportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(searchParams.get('q') ?? '');

  const defaults = useMemo(defaultDateRange, []);
  const hasUrlDateRange = searchParams.has('from') || searchParams.has('to');
  const dateFrom =
    searchParams.get('from') ?? (hasUrlDateRange ? '' : defaults.from);
  const dateTo = searchParams.get('to') ?? (hasUrlDateRange ? '' : defaults.to);
  const hasIncompleteRange = Boolean(dateFrom) !== Boolean(dateTo);
  const clinicId = searchParams.get('clinic') ?? '';
  const q = searchParams.get('q') ?? '';
  const delayMin = readIntegerDelayParam(searchParams.get('delay_min'));
  const delayMax = readIntegerDelayParam(searchParams.get('delay_max'));
  const [delayMinInput, setDelayMinInput] = useState(delayMin);
  const [delayMaxInput, setDelayMaxInput] = useState(delayMax);
  const includeTestRows = searchParams.get('include_test_rows') === 'true';
  const includeInactiveEpisodes =
    searchParams.get('include_inactive_episodes') === 'true';
  const includeImportedPatientsParam = searchParams.get(
    'include_imported_patients'
  );
  const includeImportedPatients =
    includeImportedPatientsParam !== null
      ? includeImportedPatientsParam === 'true'
      : getStoredIncludeImportedPatients();
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = readChoiceParam(
    searchParams.get('size'),
    REPORT_PAGE_SIZE_OPTIONS,
    25
  );
  const sortBy = readOptionalChoiceParam(searchParams.get('sort'), SORT_FIELDS);
  const sortOrder = readOptionalChoiceParam(
    searchParams.get('dir'),
    DIRECTIONS
  );

  useEffect(() => setSearchText(q), [q]);
  useEffect(() => setDelayMinInput(delayMin), [delayMin]);
  useEffect(() => setDelayMaxInput(delayMax), [delayMax]);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(current => {
        const next = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        if (!('page' in updates)) next.delete('page');
        return next;
      });
    },
    [setSearchParams]
  );
  const debouncedSearch = useDebounce(
    (value: string) => updateParams({ q: value.trim() || null }),
    300
  );

  const updateDateParam = useCallback(
    (field: 'from' | 'to', value: string | null) => {
      const updates: Record<string, string | null> = { [field]: value };
      if (!hasUrlDateRange) {
        // The default dates are implicit. Preserve the initial range when the
        // user edits one side for the first time.
        updates[field === 'from' ? 'to' : 'from'] =
          field === 'from' ? defaults.to : defaults.from;
      }
      updateParams(updates);
    },
    [defaults.from, defaults.to, hasUrlDateRange, updateParams]
  );

  const query = useMemo(
    () => ({
      date_from: dateFrom,
      date_to: dateTo,
      clinic_id: clinicId || null,
      q: q || null,
      delay_min: delayMin === '' ? null : Number(delayMin),
      delay_max: delayMax === '' ? null : Number(delayMax),
      ...buildReportPopulationQuery({
        includeTestRows,
        includeInactiveEpisodes,
        includeImportedPatients,
      }),
      sort_by: sortBy ?? null,
      sort_order: sortOrder ?? null,
      page,
      page_size: pageSize,
    }),
    [
      dateFrom,
      dateTo,
      clinicId,
      q,
      delayMin,
      delayMax,
      includeTestRows,
      includeInactiveEpisodes,
      includeImportedPatients,
      sortBy,
      sortOrder,
      page,
      pageSize,
    ]
  );

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    getLateStartsOptionsReportsLateStartsOptionsGet()
      .then(response => {
        if (!cancelled && response.data) setOptions(response.data);
      })
      .catch(requestError => {
        console.error('Failed to load late starts options', requestError);
      });
    return () => {
      cancelled = true;
    };
  }, [hasPermission]);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    if (!dateFrom && !dateTo) {
      setReport(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    if (hasIncompleteRange) {
      setReport(null);
      setIsLoading(false);
      setError('Select both From and To dates.');
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getLateStartsReportReportsLateStartsGet({ query })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setReport(null);
          setError(
            getBusinessErrorMessage(response) ??
              'Unable to load Late Starts report. Please try again.'
          );
          return;
        }
        setReport(response.data);
      })
      .catch(requestError => {
        console.error('Failed to load Late Starts report', requestError);
        if (!cancelled) {
          setReport(null);
          setError(
            getBusinessErrorMessage(requestError) ??
              'Unable to load Late Starts report. Please try again.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasPermission, dateFrom, dateTo, hasIncompleteRange, query]);

  const download = async () => {
    if (!dateFrom || !dateTo || hasIncompleteRange) {
      setError('Select both From and To dates.');
      return;
    }
    setIsExporting(true);
    try {
      const { page: _page, page_size: _pageSize, ...exportQuery } = query;
      void _page;
      void _pageSize;
      const response = await exportLateStartsReportReportsLateStartsExportGet({
        query: exportQuery,
        responseType: 'blob',
      });
      if (response.error || !response.data) {
        setError(
          (await getUserFacingApiMessageAsync(
            response,
            'Unable to export Late Starts report. Please try again.'
          )) ?? 'Unable to export Late Starts report. Please try again.'
        );
        return;
      }
      downloadResponseAsFile(response as never, 'late-starts-report.xlsx');
    } catch (requestError) {
      console.error('Failed to export Late Starts report', requestError);
      setError(
        (await getUserFacingApiMessageAsync(
          requestError,
          'Unable to export Late Starts report. Please try again.'
        )) ?? 'Unable to export Late Starts report. Please try again.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS))
    return <ReportsPermissionAlert />;

  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortOrder ?? 'asc' }
    : null;
  const emptyState = error ? (
    <EmptyState role='alert' title={error} description='Please try again.' />
  ) : !dateFrom || !dateTo ? (
    <EmptyState
      title='Select a date range'
      description='Choose From and To dates to load Late Starts.'
    />
  ) : (
    <EmptyState
      title='No late starts found'
      description='Adjust the dates or filters and try again.'
    />
  );
  const pagination: ReportTablePaginationState = { page, pageSize };

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start gap-3'>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void download()}
            disabled={
              isLoading ||
              isExporting ||
              !dateFrom ||
              !dateTo ||
              hasIncompleteRange
            }
          >
            <Download className='h-4 w-4' />
            {isExporting ? 'Exporting…' : 'Export'}
          </Button>
          <ReportPopulationControls
            includeTestRows={includeTestRows}
            includeInactiveEpisodes={includeInactiveEpisodes}
            includeImportedPatients={includeImportedPatients}
            onIncludeTestRowsChange={checked =>
              updateParams({ include_test_rows: checked ? 'true' : null })
            }
            onIncludeInactiveEpisodesChange={checked =>
              updateParams({
                include_inactive_episodes: checked ? 'true' : null,
              })
            }
            onIncludeImportedPatientsChange={checked => {
              setStoredIncludeImportedPatients(checked);
              updateParams({ include_imported_patients: String(checked) });
            }}
          />
        </div>
      </div>
      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}
      <div className='space-y-4 rounded-lg border bg-card p-4'>
        <div className='grid gap-3 md:grid-cols-4'>
          <div className='space-y-1 text-sm font-medium'>
            <label htmlFor='late-starts-date-from'>From</label>
            <DatePicker
              id='late-starts-date-from'
              date={parseISOToLocalDate(dateFrom)}
              datePickerSize='default'
              onDateChange={(value: Date | undefined) =>
                updateDateParam('from', value ? formatDateToISO(value) : null)
              }
              showClearButton
            />
          </div>
          <div className='space-y-1 text-sm font-medium'>
            <label htmlFor='late-starts-date-to'>To</label>
            <DatePicker
              id='late-starts-date-to'
              date={parseISOToLocalDate(dateTo)}
              datePickerSize='default'
              onDateChange={(value: Date | undefined) =>
                updateDateParam('to', value ? formatDateToISO(value) : null)
              }
              showClearButton
            />
          </div>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='late-starts-clinic'
          >
            Clinic
            <Select
              value={clinicId || ALL_CLINICS}
              onValueChange={(value: string) =>
                updateParams({ clinic: value === ALL_CLINICS ? null : value })
              }
            >
              <SelectTrigger id='late-starts-clinic' selectSize='default'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLINICS}>All clinics</SelectItem>
                {options.clinics.map(clinic => (
                  <SelectItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='late-starts-search'
          >
            Search
            <Input
              id='late-starts-search'
              value={searchText}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setSearchText(event.target.value);
                debouncedSearch(event.target.value);
              }}
              placeholder='Patient, MRN, phone, or clinic'
            />
          </label>
        </div>
        <div className='grid gap-3 md:grid-cols-2'>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='late-starts-delay-min'
          >
            Minimum delay (days)
            <Input
              id='late-starts-delay-min'
              type='number'
              step={1}
              inputMode='numeric'
              value={delayMinInput}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;
                setDelayMinInput(value);
                if (!value || INTEGER_DELAY_PATTERN.test(value)) {
                  updateParams({ delay_min: value || null });
                }
              }}
              inputSize='default'
            />
          </label>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='late-starts-delay-max'
          >
            Maximum delay (days)
            <Input
              id='late-starts-delay-max'
              type='number'
              step={1}
              inputMode='numeric'
              value={delayMaxInput}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;
                setDelayMaxInput(value);
                if (!value || INTEGER_DELAY_PATTERN.test(value)) {
                  updateParams({ delay_max: value || null });
                }
              }}
              inputSize='default'
            />
          </label>
        </div>
      </div>
      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.row_id}
        caption='Late Starts report results'
        sorting={sorting}
        onSortingChange={next =>
          updateParams(
            next
              ? { sort: next.id, dir: next.direction }
              : { sort: null, dir: null }
          )
        }
        isLoading={isLoading}
        emptyState={emptyState}
        pagination={{
          ...pagination,
          total: report?.total ?? 0,
          totalPages: Math.ceil((report?.total ?? 0) / pageSize),
        }}
        onPaginationChange={next => {
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set('page', String(next.page));
            params.set('size', String(next.pageSize));
            return params;
          });
        }}
      />
    </div>
  );
}
